// Shared Types for AMS Admin Application
// Single source of truth for both client and server type definitions
// Last updated: 2025-07-27

export type Price = number;

export enum WeightUnit {
  KG = "Kg",
  G = "g",
}

export enum PurchaseOrderState {
  DRAFT = "draft",
  ACTIVE = "active",
  RECEIVED = "received",
  ARCHIVED = "archived",
  VOID = "void",
}

export enum ProductType {
  BOOK = "Book",
  GIFT = "Gift",
  ACCESSORY = "Accessory",
  INSTRUMENT = "Instrument",
  OTHER = "Other",
}

export enum DiscountRuleType {
  FREE_DELIVERY_THRESHOLD = "FREE_DELIVERY_THRESHOLD",
}

export interface SupplierDiscountRule {
  id?: number;
  guid?: string;
  ruleType: DiscountRuleType;
  configuration: { threshold: number } | Record<string, any>;
  active: boolean;
}

export interface Supplier {
  id?: number;
  guid?: string;
  name: string;
  xeroId?: string;
  salesRep?: string;
  salesRepEmail?: string;
  salesRepPhone?: string;
  purchaseOrders?: PurchaseOrder[];
  discountRules?: SupplierDiscountRule[];
  // Relation counts returned by the supplier list; a supplier can only be
  // deleted when both are zero.
  productCount?: number;
  purchaseOrderCount?: number;
  // Set when archived: hidden from supplier pickers, kept for order history.
  archivedAt?: string | null;
}


export interface PurchaseOrder {
  id?: number;
  guid?: string;
  number: string;
  state: PurchaseOrderState;
  supplier?: Supplier;
  issueDate?: Date;
  lineItems: PurchaseOrderLineItem[];
  subTotal: number;
  taxAmount: number;
  total: number;
}

export interface PurchaseOrderLineItem {
  id?: number;
  guid?: string;
  name?: string;
  product?: Product;
  qtr?: number;
  stockOnHand?: number;
  retailPriceTmp?: number;
  pendingDelete?: boolean;
  variantSku: string;
  variantGuid?: string;
  variantUPC?: string;
  variantSupplierCode?: string;
  variantTaxRate: number;
  variantMediaURI?: string;
  description: string;
  purchaseOrder?: PurchaseOrder;
  itemCost: number;
  quantityOrdered: number;
  packSize: number;
  sequence?: number;
  receipts: PurchaseOrderLineItemReceipt[];
}

export interface PurchaseOrderLineItemReceipt {
  id?: number;
  guid?: string;
  date: Date;
  received: number;
  receivedPrice: number;
  poLineItem?: PurchaseOrderLineItem;
}

export interface SalesOrder {
  number: number;
  shopifyURL: string;
  lineItems: [
    {
      quantityOrdered: number;
      title: string;
      sku: string;
      amount: number;
    }
  ];
}

export interface Product {
  id?: number;
  guid?: string;
  name: string;
  publishedToShopify?: boolean;
  shopifyId?: string; // This is not persisted, but used to identify products in shopify when editing
  supplierGuid?: string;
  taxable: boolean;
  productType: ProductType;
  brand?: string;
  description?: string;
  tags?: string[];
  variants: ProductVariant[];
  filterFields?: string[]; // Used to differentiate variants, if more than one
  location?: string;
}

export interface ProductVariant {
  id?: number;
  name?: string;
  guid?: string;
  product?: Product;
  imageUrls?: string[];
  filterValues?: string[];
  sku: string;
  barcode?: string;
  supplierCode: string;
  weight: number;
  weightUnit: WeightUnit;
  manageStockLevels: boolean;
  stockOnHand: number;
  boughtInPacks?: boolean;
  packSize?: number; // Some variants are purchase in packs, so so maths needs to be applied when receiving
  sellable: boolean;
  buyable: boolean;
  retailPrice: number; // Prices should always be in pence to avoid using floats
  buyPrice: number;
  previousPrice?: number;
  movingAverageCost: number;
  notes?: string;
  notifyAdminOnSale?: boolean;
}

export interface Stocktake {
  guid?: string;
  location: string;
  lines: StocktakeLine[];
}

export interface StocktakeLine {
  guid?: string;
  sku: string;
  count: number;
}

export interface AuditEvent {
  id: number;
  createdAt: string;
  eventType: string;
  sourceId?: string;
  targetId?: string;
  data?: string;
}

export interface SaleEventData {
  quantity: number;
  reason?: string;
}

// Advanced product search filters. Applied server-side as WHERE clauses on
// top of the plain product list or the search results.
/** Server-side sort for the product list; null = the server's default order. */
export type ProductSortField = "name" | "productType";
export interface ProductSort {
  field: ProductSortField;
  dir: "asc" | "desc";
}

export type StockFilter = "all" | "in" | "out";
export type SaleFilter = "all" | "on" | "off";

export interface ProductSearchFilters {
  stock: StockFilter;
  /** Optional so filters persisted before it existed still load; absent = "all". */
  sale?: SaleFilter;
  supplierGuid: string | null;
  location: string | null;
}

export enum QuoteStatus {
  DRAFT = "DRAFT",
  SENT = "SENT",
  ACCEPTED = "ACCEPTED",
  DECLINED = "DECLINED",
  ARCHIVED = "ARCHIVED",
}

export enum SyncStatus {
  SYNCED = "SYNCED",
  PENDING = "PENDING",
  FAILED = "FAILED",
}

export interface Quote {
  id?: number;
  guid?: string;
  number: string;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  xeroContactId?: string;
  issueDate: Date | string;
  expiryDate?: Date | string;
  status: QuoteStatus | string;
  archived: boolean;
  xeroQuoteId?: string;
  syncStatus: SyncStatus | string;
  lastSyncError?: string;
  syncRetryCount: number;
  lastSyncAttempt?: Date | string;
  shopifyOrderId?: string;
  convertedAt?: Date | string;
  subTotal: number;
  taxAmount: number;
  total: number;
  lineItems: QuoteLineItem[];
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface QuoteLineItem {
  id?: number;
  guid?: string;
  quoteId?: number;
  variantGuid?: string;
  variant?: ProductVariant;
  description: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  taxRate: number;
  sequence?: number;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}
